import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LW_DS_CLASS } from '@/components/lw';
import { RECAPTURE_CHANGED_EVENT } from '@/services/recaptureService';
import {
  acceptLoVolumeBonus, getLoVolumeBonusAccount, getVolumeBonusBackendStatus, previewLoVolumeBonus,
  type VolumeBonusAccount, type VolumeBonusAgreement, type VolumeBonusPlan
} from '@/services/loVolumeBonusService';

export interface VolumeBonusClient {
  previewOnly?: boolean;
  getAccount: typeof getLoVolumeBonusAccount;
  preview: typeof previewLoVolumeBonus;
  accept: typeof acceptLoVolumeBonus;
}

const liveClient: VolumeBonusClient = {
  getAccount: getLoVolumeBonusAccount,
  preview: previewLoVolumeBonus,
  accept: acceptLoVolumeBonus
};
const DevPreview = import.meta.env.DEV
  ? lazy(() => import('@/components/dev/VolumeBonusDevPreview')) : null;

const usd = (amount: number) => amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const date = (value: string) => format(parseISO(value), 'MMM d, yyyy');
const message = (error: unknown) => error && typeof error === 'object' && 'message' in error
  ? String(error.message) : 'Unable to confirm this request. Please try again.';

export function Agreement({ agreement }: { agreement: VolumeBonusAgreement }) {
  return <div className="space-y-4 text-sm">
    <dl className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
      <div><dt className="text-muted-foreground">Loan officer</dt><dd className="font-medium">{agreement.recipient_name}</dd></div>
      <div><dt className="text-muted-foreground">Total volume bonus</dt><dd className="font-medium tabular-nums">{usd(agreement.amount)}</dd></div>
      <div><dt className="text-muted-foreground">Qualifying banked loans</dt><dd>{agreement.loan_count} loans purchased after {date(agreement.accepted_date)}</dd></div>
      <div><dt className="text-muted-foreground">Installments</dt><dd className="tabular-nums">{agreement.loan_count} installments of {usd(agreement.installment)}</dd></div>
      <div><dt className="text-muted-foreground">Available before this plan</dt><dd className="tabular-nums">{usd(agreement.available_before)}</dd></div>
      <div><dt className="text-muted-foreground">Available after this plan</dt><dd className="tabular-nums">{usd(agreement.available_after)}</dd></div>
    </dl>
    {agreement.requested_amount !== agreement.amount && <p>Adjusted from {usd(agreement.requested_amount)} so every installment is equal.</p>}
    <ol className="list-decimal space-y-2 pl-5">
      {agreement.terms.map(term => <li key={term}>{term}</li>)}
    </ol>
    <p className="text-xs text-muted-foreground">Agreement {agreement.agreement_version} · Pacific business date {date(agreement.accepted_date)}</p>
  </div>;
}

function Enrollment({ available, emailEnabled, onClose, onAccepted, focusTarget, client, reportPresentation = false }: {
  available: number;
  emailEnabled: boolean;
  onClose: () => void;
  onAccepted: () => void;
  focusTarget: React.RefObject<HTMLHeadingElement>;
  client: VolumeBonusClient;
  reportPresentation?: boolean;
}) {
  const [requestId] = useState(() => crypto.randomUUID());
  const [amount, setAmount] = useState(available.toFixed(2));
  const [count, setCount] = useState('10');
  const [agreement, setAgreement] = useState<VolumeBonusAgreement | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const reviewTitle = useRef<HTMLHeadingElement>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    if (agreement) {
      reviewTitle.current?.focus();
    }
  }, [agreement]);

  const review = async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const quote = await client.preview(Number(amount), Number(count));
      setAgreement(quote);
      setConfirmed(false);
    } catch (cause) {
      setError(message(cause));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const accept = async () => {
    if (!agreement || !confirmed || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await client.accept(requestId, agreement);
      onAccepted();
    } catch (cause) {
      // An explicit database rejection is safe to revise. For an unknown
      // transport result, retain the exact request and use server replay.
      const code = cause && typeof cause === 'object' && 'code' in cause ? String(cause.code) : '';
      const rejected = ['40001', '42501', 'P0001', '22023'].includes(code);
      setUncertain(!rejected);
      setError(rejected ? message(cause) : 'The response was interrupted. Your agreement may already be accepted. Retry this same request, or close and refresh your account to review it.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return <Dialog open onOpenChange={open => {
    if (!open && !busy) {
      onClose();
    }
  }}>
    <DialogContent className={`max-h-[90dvh] overflow-y-auto sm:max-w-2xl${reportPresentation ? ` ${LW_DS_CLASS} my-commissions mc-modal` : ''}`} hideCloseButton={busy}
      overlayClassName={reportPresentation ? `${LW_DS_CLASS} my-commissions mc-modal-overlay` : undefined} data-lw-overlay-layer={reportPresentation ? '' : undefined}
      onCloseAutoFocus={event => {
        event.preventDefault();
        focusTarget.current?.focus();
      }}>
      <DialogHeader>
        <DialogTitle ref={reviewTitle} tabIndex={-1}>{agreement ? 'Review your volume bonus agreement' : 'Choose your volume bonus'}</DialogTitle>
        <DialogDescription>{agreement
          ? 'Accepting starts your volume bonus plan. It does not mark any installment paid.'
          : 'Choose at least $10,000 across ten or more qualifying banked loans. Your available balance is checked again when you accept.'}</DialogDescription>
      </DialogHeader>
      {client.previewOnly && <p className="rounded-md border border-warning/40 p-3 text-sm">Development preview: acceptance stays in this tab and sends no email.</p>}
      {error && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>}
      {agreement ? <>
        <Agreement agreement={agreement} />
        <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
          <input type="checkbox" checked={confirmed} disabled={busy || uncertain} className="mt-1 h-4 w-4 shrink-0 accent-primary"
            onChange={event => setConfirmed(event.target.checked)} />
          <span>I accept this volume bonus plan: {usd(agreement.amount)} from my available LO Profitability, paid as {agreement.loan_count} installments of {usd(agreement.installment)} on my next {agreement.loan_count} qualifying banked loans. I understand the plan cannot be edited or cancelled once accepted.</span>
        </label>
        {!client.previewOnly && <p className="text-xs text-muted-foreground">{emailEnabled
          ? 'A copy of your accepted agreement will be emailed to you automatically.'
          : 'Your accepted agreement is saved to your account.'}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" disabled={busy || uncertain} onClick={() => {
            setAgreement(null);
            setError('');
            setConfirmed(false);
          }}>Change amount or loan count</Button>
          <Button disabled={!confirmed || busy} onClick={() => void accept()}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {uncertain ? 'Retry same acceptance' : 'Accept agreement'}
          </Button>
        </div>
      </> : <form className="space-y-4" onSubmit={event => {
        event.preventDefault();
        void review();
      }}>
        <p className="text-sm">Verified available LO Profitability: <strong className="tabular-nums">{usd(available)}</strong></p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="volume-amount">Amount to commit</Label>
            <Input id="volume-amount" type="number" inputMode="decimal" min="10000" max={available} step="0.01" required value={amount} disabled={busy} onChange={event => setAmount(event.target.value)} /></div>
          <div className="space-y-2"><Label htmlFor="volume-loans">Number of loans</Label>
            <Input id="volume-loans" type="number" inputMode="numeric" min="10" step="1" required value={count} disabled={busy} onChange={event => setCount(event.target.value)} /></div>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}Review agreement</Button>
        </div>
      </form>}
    </DialogContent>
  </Dialog>;
}

export function PlanProgress({ plan, previewOnly, expanded = false, hideAgreement = false }: { plan: VolumeBonusPlan; previewOnly?: boolean; expanded?: boolean; hideAgreement?: boolean }) {
  const paidCount = plan.installments.filter(installment => installment.paid).length;
  const paid = plan.installments.reduce((sum, installment) => sum + Math.round(installment.cash_paid * 100), 0) / 100;
  return <details className="rounded-md border" open={expanded || undefined}>
    <summary className="cursor-pointer px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <span className="font-medium">{usd(plan.amount)} over {plan.loan_count} loans</span>
      <span className="ml-2 text-muted-foreground">{paidCount} of {plan.loan_count} paid · {plan.installments.length} allocated{plan.departed_at ? ' · Enrollment ended on departure' : ''}</span>
    </summary>
    <div className="space-y-3 border-t p-3 text-sm">
      <p>Accepted {date(plan.accepted_date)} · Cash paid {usd(paid)}{plan.returned_amount > 0 ? ` · Unallocated profitability returned ${usd(plan.returned_amount)}` : ''}</p>
      <p className="text-xs text-muted-foreground">Allocated means compensation owed. Paid counts only fully paid installments; partial payments and voided checks update this progress.</p>
      <Table aria-label="Volume bonus installments" containerProps={{ role: 'region', 'aria-label': 'Volume bonus installment table', tabIndex: 0 }}>
        <TableHeader><TableRow><TableHead>Installment / loan</TableHead><TableHead className="text-right">Bonus</TableHead><TableHead className="text-right">Cash paid</TableHead><TableHead className="text-right">Remaining</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>{plan.installments.length === 0
          ? <TableRow><TableCell colSpan={5} className="text-muted-foreground">No installments allocated yet. Accounting assigns the next installment when an eligible Banker Recon is first finalized.</TableCell></TableRow>
          : plan.installments.map(installment => <TableRow key={installment.id}>
            <TableCell>{installment.installment_number} of {plan.loan_count}<div className="text-xs text-muted-foreground">{installment.loan_number}</div></TableCell>
            <TableCell className="text-right tabular-nums">{usd(installment.amount)}</TableCell>
            <TableCell className="text-right tabular-nums">{usd(installment.cash_paid)}</TableCell>
            <TableCell className="text-right tabular-nums">{usd(installment.remaining)}</TableCell>
            <TableCell>{installment.paid ? 'Paid' : installment.cash_paid > 0 ? 'Partially paid' : 'Allocated · owed'}</TableCell>
          </TableRow>)}</TableBody>
      </Table>
      {!hideAgreement && <details className="rounded-md border p-3"><summary className="cursor-pointer font-medium">Accepted agreement</summary><div className="mt-3"><Agreement agreement={plan.agreement} /></div></details>}
      {previewOnly ? <p className="text-xs text-muted-foreground">Development preview: no agreement email was queued.</p>
        : <p className="text-xs text-muted-foreground">Agreement email: {plan.email_status === 'accepted' ? 'accepted by email provider' : plan.email_status === 'review_required' ? 'delivery needs Accounting review' : 'awaiting provider confirmation'}. Your accepted agreement remains effective regardless of email delivery.</p>}
    </div>
  </details>;
}

export interface VolumeBonusReportPresentation {
  onAccountChange: (account: VolumeBonusAccount | null, error: string, previewOnly: boolean, visible: boolean) => void;
  onOpenDetails: (planId: string, kind: 'installments' | 'agreement') => void;
}

function ReportPlanCard({ plan, onOpen }: { plan: VolumeBonusPlan; onOpen: VolumeBonusReportPresentation['onOpenDetails'] }) {
  const paidCount = plan.installments.filter(installment => installment.paid).length;
  const paid = plan.installments.reduce((sum, installment) => sum + Math.round(installment.cash_paid * 100), 0) / 100;
  const allocated = plan.installments.reduce((sum, installment) => sum + Math.round(installment.amount * 100), 0) / 100;
  const paidWidth = Math.min(100, paid / plan.amount * 100);
  const allocatedWidth = Math.max(0, Math.min(100 - paidWidth, (allocated - paid) / plan.amount * 100));
  return <div className="mc-plan">
    <div className="mc-plan-amount"><strong>{usd(paid)}</strong><span>of {usd(plan.amount)} paid</span></div>
    <div className="mc-progress" aria-label={`${usd(paid)} paid, ${usd(allocated)} allocated, of ${usd(plan.amount)}`} role="img"><span className="mc-progress-paid" style={{ width: `${paidWidth}%` }} /><span className="mc-progress-allocated" style={{ width: `${allocatedWidth}%` }} /></div>
    <p className="mc-progress-caption"><span>{usd(allocated)} allocated</span><span>{paidCount} of {plan.loan_count} paid</span></p>
    <dl className="mc-plan-terms">
      <div><dt>Qualifying banked loans</dt><dd>{plan.loan_count} after {date(plan.accepted_date)}</dd></div>
      <div><dt>Installments</dt><dd>{plan.loan_count} installments of {usd(plan.agreement.installment)}</dd></div>
      <div><dt>Available after this plan</dt><dd>{usd(plan.agreement.available_after)}</dd></div>
    </dl>
    {plan.departed_at && <p>Enrollment ended on departure.{plan.returned_amount > 0 ? ` Unallocated profitability returned: ${usd(plan.returned_amount)}.` : ''}</p>}
    <p className="mc-muted">Allocated means compensation owed. Paid counts only fully paid installments; partial payments and voided checks update this progress.</p>
    <Button variant="ghost" className="mc-panel-link" onClick={() => onOpen(plan.id, 'installments')}><span>Installments</span> <span>{plan.installments.length} of {plan.loan_count} ›</span></Button>
    <Button variant="ghost" className="mc-panel-link" onClick={() => onOpen(plan.id, 'agreement')}><span>Accepted agreement</span> <span>›</span></Button>
    <p className="mc-muted">Agreement email: {plan.email_status === 'accepted' ? 'accepted by email provider' : plan.email_status === 'review_required' ? 'delivery needs Accounting review' : 'awaiting provider confirmation'}. Your accepted agreement remains effective regardless of email delivery.</p>
  </div>;
}

/** Lifetime account deliberately does not inherit the commission report period. */
function VolumeBonusAccountView({ recipientId, client, reportPresentation }: { recipientId: string; client: VolumeBonusClient; reportPresentation?: VolumeBonusReportPresentation }) {
  const [account, setAccount] = useState<VolumeBonusAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const generation = useRef(0);
  const accountVerified = useRef(false);
  const onAccountChange = reportPresentation?.onAccountChange;
  useEffect(() => {
    onAccountChange?.(account, error, Boolean(client.previewOnly), Boolean(account?.plans.length || account?.eligible || error || open));
  }, [account, error, client.previewOnly, onAccountChange, open]);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const result = await client.getAccount(recipientId);
      if (request === generation.current) {
        if (result !== null) {
          accountVerified.current = true;
        }
        setAccount(result);
      }
    } catch {
      if (request === generation.current && (accountVerified.current
        || (client === liveClient && getVolumeBonusBackendStatus() === 'installed'))) {
        setError('Volume bonus account unavailable. Refresh before making any new commitment.');
      }
    } finally {
      if (request === generation.current) {
        setLoading(false);
      }
    }
  }, [recipientId, client]);
  useEffect(() => {
    const requests = generation;
    void refresh();
    const update = () => {
      void refresh();
    };
    window.addEventListener(RECAPTURE_CHANGED_EVENT, update);
    window.addEventListener('focus', update);
    return () => {
      requests.current++;
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, update);
      window.removeEventListener('focus', update);
    };
  }, [refresh]);
  if (!account?.plans.length && !account?.eligible && !error && !open) {
    return null;
  }
  return <section aria-label="Volume bonus" className={reportPresentation ? 'mc-detail-card mc-volume-panel' : 'min-w-0 space-y-3'}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 ref={heading} tabIndex={-1} className={reportPresentation ? 'mc-panel-heading' : 'text-base font-semibold'}>Volume bonus</h2>
      {!reportPresentation && account?.eligible && <Button size="sm" disabled={loading || !!error} onClick={() => setOpen(true)}><Award className="mr-2 h-4 w-4" aria-hidden="true" />Congratulations — you’re eligible</Button>}
    </div>
    {error && <div role="alert" className="text-sm text-destructive">{error} <Button variant="outline" size="sm" onClick={() => void refresh()}>Refresh</Button></div>}
    {saved && <p role="status" className="text-sm">Your volume bonus agreement is accepted. No installment has been marked paid.</p>}
    {account?.eligible && <div className={reportPresentation ? 'mc-volume-eligible' : undefined}>
      {reportPresentation && <h3><Award aria-hidden="true" />Congratulations — you’re eligible</h3>}
      <p className="text-sm text-muted-foreground">You have {usd(account.profitability.available ?? 0)} in verified available LO Profitability. Choose a cash volume bonus across ten or more qualifying banked loans.</p>
      {reportPresentation && <Button disabled={loading || !!error} onClick={() => setOpen(true)}>Choose your volume bonus</Button>}
    </div>}
    {(account?.plans ?? []).map(plan => reportPresentation ? <ReportPlanCard key={plan.id} plan={plan} onOpen={reportPresentation.onOpenDetails} /> : <PlanProgress key={plan.id} plan={plan} previewOnly={client.previewOnly} />)}
    {open && account && <Enrollment available={account.profitability.available ?? 0} emailEnabled={account.email_enabled === true} focusTarget={heading} client={client} reportPresentation={Boolean(reportPresentation)}
      onClose={() => {
        setOpen(false);
        void refresh();
      }}
      onAccepted={() => {
        setOpen(false);
        setSaved(true);
        void refresh();
      }} />}
  </section>;
}

export function LoVolumeBonus({ recipientId, reportPresentation }: { recipientId: string; reportPresentation?: VolumeBonusReportPresentation }) {
  // Existing automated quality scenarios keep their supplied RPC contracts.
  // The opt-in query also lets the normal dev server exercise this wrapper there.
  const showDevControls = import.meta.env.DEV && import.meta.env.MODE !== 'test'
    && (!window.location.pathname.startsWith('/dev/ui-quality')
      || new URLSearchParams(window.location.search).get('volumeBonusPreview') === '1');
  if (DevPreview && showDevControls) {
    return <Suspense fallback={<p className="text-sm text-muted-foreground">Loading volume bonus…</p>}>
      <DevPreview key={recipientId} recipientId={recipientId}
        renderAccount={client => <VolumeBonusAccountView recipientId={recipientId} client={client} reportPresentation={reportPresentation} />}>
        <VolumeBonusAccountView recipientId={recipientId} client={liveClient} reportPresentation={reportPresentation} />
      </DevPreview>
    </Suspense>;
  }
  return <VolumeBonusAccountView recipientId={recipientId} client={liveClient} reportPresentation={reportPresentation} />;
}
